import { Meta, SectionName } from './types';
import { MarketSettings } from 'src/components/currency';
import { HrefLang, Product } from 'src/types';
import { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import {
  MarketCode,
  MarketConfig,
} from '@prisjakt/web-app-config/dist/markets/types';
import { LinkToFunc } from 'src/components/app-context';
import { sectionClassicParam, sectionMap } from './constants';

export function getMeta(product: Product): Meta {
  return {
    name: product.name,
    numberOfShops: product.prices?.meta?.itemsTotal || 0,
    brand: product.brand?.name,
    category: product.category?.name || '',
  };
}

export function getTitleTranslationKey(section: SectionName) {
  switch (section) {
    case 'properties':
      return 'product-properties.title';
    case 'reviews':
      return 'product-reviews.title';
    case 'statistics':
      return 'product-price-history.title';
    default:
      return 'product.title';
  }
}

export function getRoundedCurrency(price: number, { code }: { code: string }) {
  return parseFloat(price.toFixed(MarketSettings[code].numberOfDecimals));
}

function getMainDescriptionTranslationKey(product: Product) {
  if (
    !product.prices ||
    !product.prices.meta ||
    !product.prices.meta.itemsTotal
  ) {
    return 'product-no-shops.description';
  }

  if (!product.brand) {
    return 'product-no-brand.description';
  }

  return 'product.description';
}

export function getDescriptionTranslationKey(
  section: SectionName,
  product: Product
) {
  switch (section) {
    case 'properties':
      return 'product-properties.description';
    case 'reviews':
      return 'product-reviews.description';
    case 'statistics':
      return 'product-price-history.description';
    default:
      return getMainDescriptionTranslationKey(product);
  }
}

export function formatUrl(
  market: ClientMarket | MarketConfig | undefined,
  section: SectionName,
  url: string,
  id: string | number
) {
  if (!market) {
    return url;
  }

  if (
    !section ||
    section === 'main' ||
    (market && market.useHashForProductTabs)
  ) {
    return url;
  }

  if (/\?p=/.test(url)) {
    return url.replace(`?p=${id}`, `?${sectionClassicParam[section]}=${id}`);
  }

  if (/--p/.test(url)) {
    return `${url}/${section}`;
  }

  return url;
}

export function getLinkType(section: SectionName) {
  return sectionMap[section || 'main'];
}

/**
 *
 * @param val Expects a value between 0 and 10, which will then be normalized to 0 and 5.
 * @returns number
 */
export function getRatingValue(val: number | null | undefined) {
  return val !== undefined && val !== null
    ? parseFloat(
        (val / 2).toFixed(1) // Convert rating
      )
    : 0;
}

export function getCanonical(
  product: Product,
  section: SectionName,
  market: ClientMarket,
  linkTo: LinkToFunc
) {
  return (
    market.domain +
    (market.useHashForProductTabs
      ? linkTo('product', product)
      : linkTo(getLinkType(section), product))
  );
}

export function getHrefLang(
  product: Product,
  markets: Record<MarketCode, MarketConfig>,
  section: SectionName
) {
  return product.hrefLang
    ? product.hrefLang
        .filter(h => h !== undefined && h !== null && h.locale && h.url)
        .map(item => {
          const { locale, url } = item as HrefLang;
          return {
            locale,
            url: formatUrl(
              Object.values(markets).find(
                m => m.hrefLang && m.hrefLang.indexOf(locale) !== -1
              ),
              section,
              url,
              product.id
            ),
          };
        })
    : [];
}
